import { CreditCardIcon, ExclamationCircleIcon, HomeIcon, UserIcon } from "@heroicons/react/24/outline";
import { GenderApiValues, MAX_DATE } from "@utils/constants";
import { getGenderValue, setGenderValue } from "@utils/helpers";
import { getPayorDisplayName } from "@utils/payorConfig";
import { unwrapConnection } from "@utils/utils";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Badge, Select, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerPayorPayorTypeChoices,
  PayorType,
  UpdateLearnerDetailsMutationVariables,
  useLearnerByIdQuery,
  useRemoveLearnerPayorMutation,
  useSetLearnerPayorMutation,
  useUpdateLearnerDetailsMutation,
  useUpdateLearnerGeneralDetailsMutation,
} from "@api/graphql/types-and-hooks";
import { DoubleColumnContainer, FormContainer } from "@components/forms/FormLayout";
import SubmitButton from "@components/forms/FormSubmitButton/FormSubmitButton";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { USA_STATES_LIST } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/state";

type AddressFields = {
  address?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};

type OverviewFormShape = UpdateLearnerDetailsMutationVariables & {
  learnerDetails: UpdateLearnerDetailsMutationVariables["learnerDetails"] & AddressFields;
};

const Card: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div className="rounded-lg border border-limestone-200 bg-white p-6">
    <div className="mb-4 flex items-center gap-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-limestone-100 text-text-primary">
        {icon}
      </span>
      <h3 className="text-base font-semibold text-text-primary">{title}</h3>
    </div>
    {children}
  </div>
);

const MetadataRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-1 py-2">
    <span className="text-xs font-medium text-text-secondary">{label}</span>
    <span className="text-sm text-text-primary">{value}</span>
  </div>
);

// Sentinel id for the "None" payor option — lets someone back out of an
// accidental selection instead of being stuck once they've picked one.
const NO_PAYOR_OPTION_ID = "";

export const BasicInfo: React.FC = () => {
  const { clientId } = useParams();

  const learnerDetailsQuery = useLearnerByIdQuery(
    { learnerId: clientId ? clientId : "" },
    {
      queryKey: ["learner-by-id", clientId],
      enabled: !!clientId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const { data, refetch, isFetching } = learnerDetailsQuery;

  const updateLearnerDetailsMutation = useUpdateLearnerDetailsMutation({});
  const updateLearnerGeneralDetailsMutation = useUpdateLearnerGeneralDetailsMutation({});

  // No `isPrimary` flag exists on LearnerPayorType, so we pick the most
  // recently created active payor as "primary" for display.
  const learnerPayors = unwrapConnection(data?.learnerById?.learnerpayorSet);
  const activePayors = learnerPayors?.filter((p) => p.isActive) ?? [];
  const primaryPayor = [...activePayors].sort(
    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
  )[0];

  const setLearnerPayorMutation = useSetLearnerPayorMutation({});
  const removeLearnerPayorMutation = useRemoveLearnerPayorMutation({});

  // `refetch()` below re-fetches the single query this whole form shares, so
  // its `isFetching` flips true for the entire round-trip — which would grey
  // out every other field wired to `formLoading` (Basics, Home address) even
  // though only the payor changed. This flag tells `formLoading` to ignore
  // `isFetching` while it's true, so a payor pick only affects the payor card.
  const isPayorRefetchRef = useRef(false);

  // clearPayor/switchPayor fire multiple removeLearnerPayor calls in parallel
  // on the same mutation hook instance, so that hook's own `isPending` only
  // reflects whichever call it dispatched last — it can flip back to false
  // while earlier removes are still in flight. This flag is set for the
  // whole clearPayor/switchPayor operation (mutations + trailing refetch)
  // instead, so the select can't be reopened mid-flight.
  const [isPayorMutating, setIsPayorMutating] = useState(false);
  const isPayorSaving = isPayorMutating || (isFetching && isPayorRefetchRef.current);

  // `selected` below is otherwise derived purely from server data
  // (`primaryPayor`), which only updates once `refetch()` resolves at the end
  // of `handlePayorSelect`. Without this, the select kept showing the
  // previous payor for the entire remove+set round-trip, then snapped to the
  // new one — reading as a confusing flash back to the old value. This
  // mirrors the clicked item immediately and hands off to server data (via
  // the `undefined` reset in `finally`, once refetch has landed) once it's
  // confirmed.
  const [optimisticPayorSelection, setOptimisticPayorSelection] = useState<SelectItem | null | undefined>(undefined);

  // If a switch's setLearnerPayor call fails after the old payors were already
  // removed, there's no "undo remove" — the only way back is to re-assign the
  // original payors via setLearnerPayor. If that also fails, say so explicitly
  // rather than leaving the client silently payor-less.
  const restorePreviousPayors = async (payorTypes: ApiLearnerPayorPayorTypeChoices[]) => {
    if (!clientId || payorTypes.length === 0) return;
    try {
      const restoreResult = await setLearnerPayorMutation.mutateAsync({
        learnerId: clientId,
        payorTypes: payorTypes as unknown as PayorType[],
      });
      if (!restoreResult.setLearnerPayor?.status) {
        notifyError("Couldn't restore the previous payor after the update failed — please check this client's payor.");
      }
    } catch {
      notifyError("Couldn't restore the previous payor after the update failed — please check this client's payor.");
    }
  };

  // Removes every given active payor in parallel and reports which of them
  // actually came off — since each call is independent, one can soft-fail or
  // throw while the others succeed, and callers need to know exactly which
  // payors are now gone rather than assuming all-or-nothing.
  const removeActivePayors = async (payorTypes: ApiLearnerPayorPayorTypeChoices[]) => {
    const settled = await Promise.allSettled(
      payorTypes.map((payorType) =>
        removeLearnerPayorMutation.mutateAsync({
          learnerId: clientId as string,
          payorType: payorType as unknown as PayorType,
        }),
      ),
    );
    const removedPayorTypes: ApiLearnerPayorPayorTypeChoices[] = [];
    let errorMessage: string | undefined;
    settled.forEach((outcome, index) => {
      if (outcome.status === "fulfilled" && outcome.value.removeLearnerPayor?.status) {
        removedPayorTypes.push(payorTypes[index]);
      } else if (outcome.status === "fulfilled") {
        errorMessage ??= outcome.value.removeLearnerPayor?.errors?.[0]?.messages?.[0] ?? undefined;
      }
    });
    return { removedPayorTypes, allRemoved: removedPayorTypes.length === payorTypes.length, errorMessage };
  };

  // The "None" option lets someone back out of an accidental selection — it
  // only clears the existing payor, it never calls setLearnerPayor. The
  // select only ever shows one "primary" payor, but a learner can have more
  // than one active row (e.g. a sync-created one alongside a portal-created
  // one) — clearing all of them keeps a stale row from resurfacing as primary
  // after refetch.
  const clearPayor = async () => {
    if (!clientId || activePayors.length === 0) return;
    setOptimisticPayorSelection(null);
    setIsPayorMutating(true);
    // No success toast here — the payor card saves on selection, but the
    // rest of the Overview form (Basics/Address) still saves via the page's
    // "Update" button. A toast here could read as "you're done" and nudge
    // someone away from hitting Update for the rest of their edits.
    try {
      const { allRemoved, errorMessage } = await removeActivePayors(activePayors.map((payor) => payor.payorType));
      if (!allRemoved) {
        notifyError(errorMessage ?? "Failed to remove payor.");
      }
    } catch {
      notifyError("Failed to remove payor.");
    } finally {
      isPayorRefetchRef.current = true;
      await refetch();
      isPayorRefetchRef.current = false;
      setOptimisticPayorSelection(undefined);
      setIsPayorMutating(false);
    }
  };

  const switchPayor = async (newPayorType: ApiLearnerPayorPayorTypeChoices) => {
    if (!clientId) return;
    const previousPayorTypes = activePayors.map((payor) => payor.payorType);

    setOptimisticPayorSelection({ id: newPayorType, primary: getPayorDisplayName(newPayorType) });
    setIsPayorMutating(true);
    try {
      // setLearnerPayor only upserts the payors it's given — it doesn't
      // deactivate others, so switching the single "Primary payor" select
      // also removes every previously active payor, not just the one shown
      // as primary.
      if (previousPayorTypes.length > 0) {
        const { removedPayorTypes, allRemoved, errorMessage } = await removeActivePayors(previousPayorTypes);
        if (!allRemoved) {
          notifyError(errorMessage ?? "Failed to update payor.");
          // Restore whatever did come off before the failure — a partial
          // remove shouldn't leave those payors gone with nothing set back.
          if (removedPayorTypes.length > 0) {
            await restorePreviousPayors(removedPayorTypes);
          }
          return;
        }
      }

      const result = await setLearnerPayorMutation.mutateAsync({
        learnerId: clientId,
        payorTypes: [newPayorType as unknown as PayorType],
      });
      if (!result.setLearnerPayor?.status) {
        notifyError(result.setLearnerPayor?.errors?.[0]?.messages?.[0] ?? "Failed to update payor.");
        if (previousPayorTypes.length > 0) {
          await restorePreviousPayors(previousPayorTypes);
        }
      }
    } catch {
      notifyError("Failed to update payor.");
      if (previousPayorTypes.length > 0) {
        await restorePreviousPayors(previousPayorTypes);
      }
    } finally {
      isPayorRefetchRef.current = true;
      await refetch();
      isPayorRefetchRef.current = false;
      setOptimisticPayorSelection(undefined);
      setIsPayorMutating(false);
    }
  };

  const handlePayorSelect = async (item: SelectItem) => {
    if (item.id === NO_PAYOR_OPTION_ID) {
      await clearPayor();
      return;
    }
    const newPayorType = item.id as ApiLearnerPayorPayorTypeChoices;
    if (newPayorType === primaryPayor?.payorType) return;
    await switchPayor(newPayorType);
  };

  const serverPayorItem = primaryPayor
    ? { id: primaryPayor.payorType, primary: getPayorDisplayName(primaryPayor.payorType) }
    : null;
  const selectedPayorItem = optimisticPayorSelection !== undefined ? optimisticPayorSelection : serverPayorItem;

  // `externalId` is set when a learner originated in an EHR system and was
  // imported; absent means the learner was created directly in Frontera.
  const isEhrImported = !!data?.learnerById?.externalId;
  const createdDate = data?.learnerById?.createdDate;

  const { formState, watch, onSubmit, RegisteredFormInput, RegisteredFormSelected, reset } =
    useFormUtils<OverviewFormShape>({
      mutationFn: async (params) => {
        const formattedGender = setGenderValue(params.learnerDetails.gender);
        // LearnerDetailsInput has no address fields, so updateLearnerDetails
        // still can't accept them; updateLearnerGeneralDetails carries address
        // plus the same identity fields, so we fire both from one submit.
        const { address, addressLine2, city, state, zip, ...identityFields } = params.learnerDetails;
        const learner = clientId || "";

        const [detailsSettled, generalSettled] = await Promise.allSettled([
          updateLearnerDetailsMutation.mutateAsync({
            learnerDetails: { ...identityFields, gender: formattedGender },
            learner,
          }),
          updateLearnerGeneralDetailsMutation.mutateAsync({
            learnerDetails: {
              firstName: identityFields.firstName,
              lastName: identityFields.lastName,
              birthDate: identityFields.dob,
              gender: formattedGender,
              specifyGender: identityFields.specifyGender,
              address,
              addressLine2,
              city,
              state,
              zip,
            },
            learner,
          }),
        ]);

        const detailsOk = detailsSettled.status === "fulfilled" && !!detailsSettled.value.updateLearnerDetails?.status;
        const generalOk =
          generalSettled.status === "fulfilled" && !!generalSettled.value.updateLearnerGeneralDetails?.status;

        if (detailsOk && generalOk) {
          const message =
            (detailsSettled.status === "fulfilled" && detailsSettled.value.updateLearnerDetails?.message) ||
            (generalSettled.status === "fulfilled" && generalSettled.value.updateLearnerGeneralDetails?.message) ||
            "";
          notifySuccess(message);
        } else {
          const message =
            (detailsSettled.status === "fulfilled" &&
              !detailsOk &&
              detailsSettled.value.updateLearnerDetails?.message) ||
            (generalSettled.status === "fulfilled" &&
              !generalOk &&
              generalSettled.value.updateLearnerGeneralDetails?.message) ||
            "Something went wrong, please try again later.";
          notifyError(message);
        }

        await refetch();
      },
    });

  useEffect(() => {
    if (data && data.learnerById?.learnermetadata) {
      const learner = data.learnerById?.learnermetadata;
      const formattedGender = getGenderValue(learner.gender);

      reset({
        learnerDetails: {
          firstName: learner?.firstName,
          lastName: learner?.lastName,
          dob: learner?.birthDate,
          gender: formattedGender,
          address: learner?.address ?? "",
          addressLine2: learner?.addressLine2 ?? "",
          city: learner?.city ?? "",
          state: learner?.state ?? "",
          zip: learner?.zip ?? "",
        },
      });
    }
  }, [data, reset]);

  const formLoading = isFetching && !isPayorRefetchRef.current;
  const genderWatch = watch("learnerDetails.gender");

  return (
    <div className="w-full pt-4">
      <FormContainer onSubmit={onSubmit}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-6 lg:col-span-2">
            <Card title="Basics" icon={<UserIcon className="h-5 w-5" />}>
              <DoubleColumnContainer>
                <RegisteredFormInput
                  isLoading={formLoading}
                  formKey="learnerDetails.firstName"
                  inputSize="full"
                  formState={formState}
                  label="First Name"
                />
                <RegisteredFormInput
                  formKey="learnerDetails.lastName"
                  inputSize="full"
                  formState={formState}
                  label="Last Name"
                />
              </DoubleColumnContainer>
              <DoubleColumnContainer>
                <RegisteredFormInput
                  isLoading={formLoading}
                  formKey="learnerDetails.dob"
                  inputSize="full"
                  type="date"
                  formState={formState}
                  label="Date of Birth"
                  max={MAX_DATE}
                />
              </DoubleColumnContainer>
              <DoubleColumnContainer>
                <RegisteredFormSelected
                  isLoading={formLoading}
                  formKey="learnerDetails.gender"
                  formState={formState}
                  placeholderText={""}
                  title="Gender"
                  required={false}
                  items={
                    Object.values(GenderApiValues).map((o) => ({
                      primary: getGenderValue(o),
                    })) as SelectItem[]
                  }
                />
                {genderWatch === "Gender identity is not listed" && (
                  <RegisteredFormInput
                    formKey="learnerDetails.specifyGender"
                    formState={formState}
                    required={false}
                    label="Specify Gender"
                  />
                )}
              </DoubleColumnContainer>
            </Card>

            <Card title="Home address" icon={<HomeIcon className="h-5 w-5" />}>
              <DoubleColumnContainer>
                <RegisteredFormInput
                  isLoading={formLoading}
                  formKey="learnerDetails.address"
                  inputSize="full"
                  formState={formState}
                  required={false}
                  label="Address"
                />
                <RegisteredFormInput
                  isLoading={formLoading}
                  formKey="learnerDetails.addressLine2"
                  inputSize="full"
                  formState={formState}
                  required={false}
                  label="Address Line 2"
                />
              </DoubleColumnContainer>
              <DoubleColumnContainer>
                <RegisteredFormInput
                  isLoading={formLoading}
                  formKey="learnerDetails.city"
                  inputSize="full"
                  formState={formState}
                  required={false}
                  label="City"
                />
                <RegisteredFormSelected
                  isLoading={formLoading}
                  formKey="learnerDetails.state"
                  formState={formState}
                  placeholderText=""
                  title="State"
                  required={false}
                  items={USA_STATES_LIST.map((o) => ({ primary: o })) as SelectItem[]}
                />
              </DoubleColumnContainer>
              <DoubleColumnContainer>
                <RegisteredFormInput
                  isLoading={formLoading}
                  formKey="learnerDetails.zip"
                  inputSize="full"
                  formState={formState}
                  required={false}
                  label="ZIP"
                />
              </DoubleColumnContainer>
            </Card>

            <Card title="Payor" icon={<CreditCardIcon className="h-5 w-5" />}>
              <p className="mb-4 text-sm text-text-secondary">
                Assigning a payor to this client helps ensure the right report template context is used in their
                reports.
                {/* TODO(PROA-2034): re-enable once the HubSpot payor article is live — re-add the
                PAYOR_HELP_ARTICLE_URL import from @utils/constants.
                {" "}
                <a
                  className="text-interaction-primary no-underline cursor-pointer"
                  href={PAYOR_HELP_ARTICLE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more.
                </a>
                */}
              </p>
              {!selectedPayorItem && !isFetching && (
                <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                  <p className="text-sm text-amber-800">
                    No payor assigned. Assigning a payor ensures the right report template is served for this client.
                  </p>
                </div>
              )}
              <Select
                title="Primary payor"
                placeholderText="No payor selected"
                disabled={isPayorSaving}
                items={[
                  { id: NO_PAYOR_OPTION_ID, primary: "None" },
                  ...Object.values(ApiLearnerPayorPayorTypeChoices).map((id) => ({
                    id,
                    primary: getPayorDisplayName(id),
                  })),
                ]}
                selected={selectedPayorItem}
                setSelected={handlePayorSelect}
              />
            </Card>

            <div>
              <SubmitButton isLoading={formState.isSubmitting} buttonText="Update" />
            </div>
          </div>

          <div className="lg:col-span-1">
            <Card title="Profile details" icon={<UserIcon className="h-5 w-5" />}>
              <MetadataRow
                label="Created on"
                value={createdDate ? format(new Date(createdDate), "MMM d, yyyy") : "—"}
              />
              <MetadataRow
                label="Source"
                value={<Badge appearance="neutral" text={isEhrImported ? "EHR Imported" : "Frontera"} />}
              />
            </Card>
          </div>
        </div>
      </FormContainer>
    </div>
  );
};
